import React from 'react';
import { Link, useParams, useOutletContext, useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    Package as PackageIcon,
    CheckCircle2,
    Truck,
    PackageX,
    Info,
} from 'lucide-react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

import { productsApi } from '../lib/productsApi';
import PeriodPicker from '../components/products/PeriodPicker';
import ProductImage from '../components/products/ProductImage';
import { TableRowSkeleton } from '../components/common/TableSkeleton';
import EmptyState from '../components/common/EmptyState';

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------
const fmtInt = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtPct = (n) => `${Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}%`;
const fmtDayFR = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }); } catch { return iso; }
};

function KpiCard({ label, value, sublabel, Icon, iconColor, testId }) {
    return (
        <div
            data-testid={testId}
            className="bg-white rounded-xl border border-[#E5E7EB] p-4 shadow-[0_1px_2px_rgba(16,24,40,0.05)] flex flex-col gap-2 min-h-[112px]"
        >
            <div className="flex items-start justify-between">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold">{label}</div>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${iconColor}15` }}>
                    <Icon size={18} strokeWidth={1.75} style={{ color: iconColor }} />
                </div>
            </div>
            <div className="text-2xl font-bold text-[#1A1D2E]" data-testid={`${testId}-value`}>{value}</div>
            {sublabel && <div className="text-[12px] text-[#6B7280]">{sublabel}</div>}
        </div>
    );
}

function ChartTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="bg-white border border-[#E5E7EB] rounded-lg shadow-nivo-hover px-3 py-2 text-xs">
            <div className="text-[#6B7280]">{fmtDayFR(p.date)}</div>
            <div className="text-[#1A1D2E] font-semibold">{fmtInt(p.count)} commandes</div>
        </div>
    );
}

// -----------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------
export default function ProduitDetailPage() {
    const { id } = useParams();
    const { user: _user } = useOutletContext();
    const navigate = useNavigate();

    const [period, setPeriod] = React.useState({ period: 'this_month' });
    const [product, setProduct] = React.useState(null);
    const [stats, setStats] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [prodRes, statsRes] = await Promise.all([
                productsApi.get(id),
                productsApi.stats(id, period),
            ]);
            setProduct(prodRes.product);
            setStats(statsRes);
        } catch (e) {
            if (e.status === 404) {
                setError('Produit introuvable.');
            } else {
                setError(e.detail || e.message || 'Erreur');
            }
        } finally {
            setLoading(false);
        }
    }, [id, period]);

    React.useEffect(() => { load(); }, [load]);

    const kpis = stats?.kpis || {};
    const performance = stats?.performance || [];
    const series = (stats?.series || []).map((s) => ({ ...s, label: fmtDayFR(s.date) }));
    const hasChart = series.some((s) => s.count > 0);

    return (
        <div data-testid="produit-detail-page" className="space-y-5">
            {/* Breadcrumb */}
            <button
                type="button"
                data-testid="back-to-products"
                onClick={() => navigate('/produits')}
                className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7280] hover:text-[#4B3FD8] transition-colors"
            >
                <ChevronLeft size={16} strokeWidth={2} />
                Retour aux produits
            </button>

            {/* Hero */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 md:p-6 shadow-[0_1px_2px_rgba(16,24,40,0.04)] flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-[220px] aspect-square shrink-0">
                    <ProductImage src={product?.image_url} alt={product?.name || ''} />
                </div>
                <div className="flex-1 flex flex-col justify-between gap-4 min-w-0">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                            {product?.is_personalized && (
                                <span
                                    data-testid="detail-badge-personnalise"
                                    className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold text-[#4B3FD8] bg-[#4B3FD8]/15 border border-[#4B3FD8]/20"
                                >
                                    Personnalisé
                                </span>
                            )}
                            {product?.shopify_product_id && (
                                <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold text-[#6B7280] bg-[#F5F6FA] border border-[#E5E7EB]">
                                    Shopify ID · {product.shopify_product_id}
                                </span>
                            )}
                        </div>
                        <h1
                            data-testid="product-name"
                            className="text-[22px] md:text-[26px] font-bold text-[#1A1D2E] leading-tight break-words"
                        >
                            {loading ? 'Chargement…' : (product?.name || '—')}
                        </h1>
                    </div>
                    <div className="flex items-start justify-end">
                        <PeriodPicker value={period} onChange={setPeriod} testId="detail-period" />
                    </div>
                </div>
            </div>

            {error && (
                <div data-testid="detail-error" className="bg-white border border-[#EF4444]/30 rounded-xl px-4 py-3 text-sm text-[#EF4444]">
                    {error}
                </div>
            )}

            {/* KPI cards */}
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <KpiCard testId="kpi-total"           label="Commandes"           value={fmtInt(kpis.total)}              Icon={PackageIcon}  iconColor="#4B3FD8" />
                <KpiCard testId="kpi-confirmation"    label="Taux de confirmation" value={fmtPct(kpis.confirmation_rate)} Icon={CheckCircle2} iconColor="#10B981" sublabel={`${fmtInt(kpis.confirmed)} confirmées`} />
                <KpiCard testId="kpi-delivery"        label="Taux de livraison"    value={fmtPct(kpis.delivery_rate)}     Icon={Truck}        iconColor="#3B82F6" sublabel={`${fmtInt(kpis.in_delivery)} in delivery`} />
                <KpiCard testId="kpi-return"          label="Taux de retour"       value={fmtPct(kpis.return_rate)}       Icon={PackageX}     iconColor="#EF4444" sublabel={`${fmtInt(kpis.returned)} retournées`} />
            </div>

            {/* Chart + table row */}
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
                {/* Daily chart */}
                <div className="xl:col-span-3 bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] overflow-hidden">
                    <div className="px-4 py-3 border-b border-[#E5E7EB]">
                        <h3 className="text-base font-semibold text-[#1A1D2E]">Évolution des commandes</h3>
                    </div>
                    <div className="h-[280px]">
                        {loading ? (
                            <div className="h-full p-2">
                                <div className="h-full w-full rounded-lg bg-[#F5F6FA] overflow-hidden relative">
                                    <span className="absolute inset-0 block animate-shimmer bg-gradient-to-r from-transparent via-white to-transparent" />
                                </div>
                            </div>
                        ) : !hasChart ? (
                            <div data-testid="detail-chart-empty" className="h-full flex flex-col items-center justify-center gap-1 text-sm text-[#6B7280]">
                                <div className="text-[#1A1D2E] font-semibold text-[13px]">Aucune donnée pour cette période</div>
                                <div className="text-xs">Ajustez la période pour voir l'évolution.</div>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={series} margin={{ top: 12, right: 12, left: -8, bottom: 4 }}>
                                    <defs>
                                        <linearGradient id="prod-daily-fill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#4B3FD8" stopOpacity={0.28} />
                                            <stop offset="100%" stopColor="#4B3FD8" stopOpacity={0.02} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid stroke="#E5E7EB" strokeDasharray="4 4" vertical={false} />
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fill: '#6B7280', fontSize: 11 }}
                                        axisLine={{ stroke: '#E5E7EB' }}
                                        tickLine={false}
                                        interval="preserveStartEnd"
                                        minTickGap={20}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fill: '#6B7280', fontSize: 11 }}
                                        axisLine={{ stroke: '#E5E7EB' }}
                                        tickLine={false}
                                        width={38}
                                    />
                                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: '#4B3FD8', strokeOpacity: 0.15, strokeWidth: 2 }} />
                                    <Area
                                        type="monotone"
                                        dataKey="count"
                                        stroke="#4B3FD8"
                                        strokeWidth={2}
                                        fill="url(#prod-daily-fill)"
                                        dot={{ r: 3, fill: '#4B3FD8', strokeWidth: 0 }}
                                        activeDot={{ r: 5, fill: '#4B3FD8', stroke: '#FFFFFF', strokeWidth: 2 }}
                                        isAnimationActive={false}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Performance table */}
                <div className="xl:col-span-2 bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] overflow-hidden flex flex-col">
                    <div className="px-4 py-3 border-b border-[#E5E7EB]">
                        <h3 className="text-base font-semibold text-[#1A1D2E]">Performance par Call Center</h3>
                    </div>
                    <div className="overflow-x-auto flex-1">
                        <table className="w-full text-sm min-w-[560px]" data-testid="detail-perf-table">
                            <thead>
                                <tr className="bg-[#F5F6FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                                    <th className="text-left px-3 py-2 font-semibold">Call Center</th>
                                    <th className="text-right px-3 py-2 font-semibold">Total</th>
                                    <th className="text-right px-3 py-2 font-semibold">Confirmé</th>
                                    <th className="text-right px-3 py-2 font-semibold">In Del.</th>
                                    <th className="text-right px-3 py-2 font-semibold">Conf.</th>
                                    <th className="text-right px-3 py-2 font-semibold">Livr.</th>
                                    <th className="text-right px-3 py-2 font-semibold">Ret.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <TableRowSkeleton rows={3} columns={7} testId="detail-perf-skeleton" />
                                )}
                                {!loading && performance.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="py-2">
                                            <EmptyState
                                                testId="detail-perf-empty"
                                                Icon={Info}
                                                title="Aucune donnée sur cette période"
                                            />
                                        </td>
                                    </tr>
                                )}
                                {!loading && performance.map((r, idx) => (
                                    <tr
                                        key={`${r.call_center_id || 'none'}-${idx}`}
                                        data-testid="detail-perf-row"
                                        className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F6FA]'}
                                    >
                                        <td className="px-3 py-2">
                                            <div className="flex flex-col">
                                                <span className="font-semibold text-[#1A1D2E]">{r.call_center_label}</span>
                                                {r.call_center_name !== r.call_center_label && (
                                                    <span className="text-[11px] text-[#6B7280]">{r.call_center_name}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-right font-medium">{fmtInt(r.total)}</td>
                                        <td className="px-3 py-2 text-right text-[#10B981] font-medium">{fmtInt(r.confirmed)}</td>
                                        <td className="px-3 py-2 text-right text-[#3B82F6] font-medium">{fmtInt(r.in_delivery)}</td>
                                        <td className="px-3 py-2 text-right">{fmtPct(r.confirmation_rate)}</td>
                                        <td className="px-3 py-2 text-right">{fmtPct(r.delivery_rate)}</td>
                                        <td className="px-3 py-2 text-right">{fmtPct(r.return_rate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
